import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import cx from 'classnames';
import LinkModel from '../../../models/link';
import CSSFilter from '../../../helpers/css-filter';
import checkParam from '../../../helpers/url-redirect';
import PageTransition, { TRANSITION_MODES } from '../transition/transition';
import styles from './linkButton.module.scss';

type Props = {
  data?: LinkModel | null;
  linkClass?: string;
  iconClass?: string;
  children?: React.ReactNode;
  defaultColor?: string;
  onClick?: (arg1: object, arg2: string) => void;
};

export default ({
  data,
  linkClass,
  iconClass,
  children,
  defaultColor = '#000',
  onClick
}: Props) => {
  if (!data) return null;

  const {
    icon,
    route,
    label,
    template,
    customStyles,
    usePageTransition,
    analyticsAttributes
  } = data;
  const onClientSide = typeof document !== 'undefined';

  const [filter, setFilter] = useState(CSSFilter(defaultColor));
  const [linkWasActivated, setLinkWasActivated] = useState(false);

  useEffect(() => {
    if (customStyles?.color) {
      setFilter(CSSFilter(customStyles.color));
    }
  }, []);

  useEffect(() => {
    const deactivateLink = (event: any) => {
      if (event.persisted) setLinkWasActivated(false);
    };

    if (onClientSide) {
      window.addEventListener('pageshow', deactivateLink);
    }

    return () => {
      window.removeEventListener('pageshow', deactivateLink);
    };
  }, []);

  const clickHandler = (event: any) => {
    event.preventDefault();
    const processedRoute = checkParam(route, 'amexBikeBenefit');

    if (onClick) {
      onClick(event, processedRoute);
    } else if (onClientSide) {
      setLinkWasActivated(true);

      if (usePageTransition) {
        setTimeout(() => {
          document.location.href = processedRoute;
        }, 1000);
      } else {
        document.location.href = processedRoute;
      }
    }
  };

  return (
    <>
      <a
        role="link"
        onClick={clickHandler}
        onKeyDown={clickHandler}
        aria-label={label}
        tabIndex={0}
        className={
          linkClass ||
          cx(styles.linkButton__link, {
            [styles[`linkButton__link--${template}`]]: template
          })
        }
        style={customStyles}
        {...(analyticsAttributes || {})}
      >
        {children ||
          (icon ? (
            <div
              className={iconClass || styles.linkButton__icon}
              style={filter}
            >
              <Image alt={icon?.alt} src={icon?.src} layout="fill" />
            </div>
          ) : (
            label
          ))}
      </a>
      {usePageTransition && (
        <PageTransition
          activated={linkWasActivated}
          mode={TRANSITION_MODES.out}
        />
      )}
    </>
  );
};
